import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { tenantSettingsRepository } from '@/services';
import { toast } from 'sonner';

export interface EmbalagemPadrao {
  largura: number;
  altura: number;
  comprimento: number;
  peso: number;
}

export interface ShippingCustomization {
  frete_gratis_enabled: boolean;
  frete_fixo_enabled: boolean;
  frete_fixo_values: Record<string, number | null>;
  entrega_local_enabled: boolean;
  retirada_loja_enabled: boolean;
  embalagem_padrao: EmbalagemPadrao;
}

const DEFAULT_EMBALAGEM: EmbalagemPadrao = {
  largura: 40,
  altura: 10,
  comprimento: 30,
  peso: 2,
};

const DEFAULT_SETTINGS: ShippingCustomization = {
  frete_gratis_enabled: false,
  frete_fixo_enabled: false,
  frete_fixo_values: {},
  entrega_local_enabled: false,
  retirada_loja_enabled: true,
  embalagem_padrao: DEFAULT_EMBALAGEM,
};

export function useShippingCustomization() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: tenantId, isLoading: isLoadingTenant } = useQuery({
    queryKey: ['tenant-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await tenantSettingsRepository.getByProfileUserId(user!.id);
      if (error) throw error;
      return data?.tenant_id;
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10,
  });

  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['shipping-customization', tenantId],
    queryFn: async () => {
      const { data, error } = await tenantSettingsRepository.get(tenantId!);
      if (error) throw error;
      const rawEmbalagem = data.embalagem_padrao as EmbalagemPadrao | null;
      return {
        frete_gratis_enabled: data.frete_gratis_enabled ?? false,
        frete_fixo_enabled: data.frete_fixo_enabled ?? false,
        frete_fixo_values: (data.frete_fixo_values as Record<string, number | null>) ?? {},
        entrega_local_enabled: data.entrega_local_enabled ?? false,
        retirada_loja_enabled: data.retirada_loja_enabled ?? true,
        embalagem_padrao: rawEmbalagem && rawEmbalagem.largura ? rawEmbalagem : DEFAULT_EMBALAGEM,
      } as ShippingCustomization;
    },
    enabled: !!tenantId,
    staleTime: 1000 * 60 * 10,
  });

  const saveMutation = useMutation({
    mutationFn: async (updates: Partial<ShippingCustomization>) => {
      const { error } = await tenantSettingsRepository.update(tenantId!, updates);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-customization', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
      toast.success('Configurações de frete salvas!');
    },
    onError: () => {
      toast.error('Erro ao salvar configurações de frete.');
    },
  });

  return {
    tenantId,
    settings: settings ?? DEFAULT_SETTINGS,
    isLoading: isLoadingTenant || isLoadingSettings,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
}
